import { Alert, AlertDescription, cn } from '@design-system';

import { Mail, MailCheck, ChevronRight, Info } from 'lucide-react';

import { useSelector } from 'react-redux';

import { selectSessionAdvisor } from '@client/model';
import { getOauthProviderIcon } from '@client/util/getOauthProviderIcon';
import { OAuthRedirectPage, OAUTH_REDIRECT_FROM_PAGE_KEY } from '@client/components/OAuthCallback';

interface ProviderOptionProps {
  provider: OAuthProvider;
  title: string;
  description: string;
  isConnected: boolean;
  connectedEmail?: string;
  onClick: () => void;
}

function ProviderOption({
  provider,
  title,
  description,
  isConnected,
  connectedEmail,
  onClick,
}: ProviderOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'border-border bg-background group flex w-full cursor-pointer items-center gap-4 rounded-lg border p-4 text-left transition-all',
        {
          'border-green-600/20 bg-green-600/5 text-green-800': isConnected,
          'hover:border-primary hover:bg-primary/5': !isConnected,
        },
      )}
    >
      <div
        className={cn(
          'bg-muted [&>svg]:mr-0! grid size-12 shrink-0 place-items-center rounded-full transition-all',
          {
            'bg-green-600/20': isConnected,
            'group-hover:bg-primary/10': !isConnected,
          },
        )}
      >
        {getOauthProviderIcon(provider)}
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-gray-950">{title}</h3>
        {isConnected ? (
          <div className="text-foreground flex items-center gap-1 text-sm">
            <MailCheck className="size-4" />
            <span>Connected to {connectedEmail}</span>
          </div>
        ) : (
          <p className="text-foreground text-sm">{description}</p>
        )}
      </div>
      {!isConnected && (
        <ChevronRight className="group-hover:text-primary text-muted-foreground size-5 transition-all" />
      )}
    </button>
  );
}

export function AdvisorOnboardingConnectEmailStep() {
  const advisor = useSelector(selectSessionAdvisor);
  const googleSettings = advisor?.advisorOauthSettings?.google;
  const microsoftSettings = advisor?.advisorOauthSettings?.microsoft;

  const isGoogleConnected =
    googleSettings?.isConnected ??
    !!(googleSettings?.userEmail && googleSettings?.createdAtDateTime);
  const isMicrosoftConnected =
    microsoftSettings?.isConnected ??
    !!(microsoftSettings?.userEmail && microsoftSettings?.createdAtDateTime);
  const isAnyConnected = isGoogleConnected || isMicrosoftConnected;

  function handleConnect(provider: OAuthProvider) {
    const settings = provider === 'google' ? googleSettings : microsoftSettings;

    if (settings?.consentUrl) {
      localStorage.setItem(OAUTH_REDIRECT_FROM_PAGE_KEY, OAuthRedirectPage.ADVISOR_ONBOARDING);
      window.location.href = settings.consentUrl;
    }
  }

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="w-full max-w-xl">
        <div className="mb-8 text-center">
          <div className="bg-accent mx-auto mb-4 flex size-14 items-center justify-center rounded-full">
            <Mail className="size-6 text-gray-950" />
          </div>
          <h1 className="text-2xl font-bold text-gray-950">Connect Your Email</h1>
          <p className="text-foreground mt-2 text-balance">
            Send personalized emails directly from your email account to strengthen client
            relationships and improve client engagement.
          </p>
        </div>

        <div className="space-y-3">
          {(!isAnyConnected || isGoogleConnected) && (
            <ProviderOption
              provider="google"
              title="Google"
              description="Connect your Gmail account"
              isConnected={isGoogleConnected}
              connectedEmail={googleSettings?.userEmail}
              onClick={() => handleConnect('google')}
            />
          )}

          {(!isAnyConnected || isMicrosoftConnected) && (
            <ProviderOption
              provider="microsoft"
              title="Microsoft Outlook"
              description="Connect your Outlook account"
              isConnected={isMicrosoftConnected}
              connectedEmail={microsoftSettings?.userEmail}
              onClick={() => handleConnect('microsoft')}
            />
          )}
        </div>

        {isAnyConnected && (
          <Alert className="mt-4">
            <Info className="size-4" />
            <AlertDescription>
              You can change your email provider anytime in your profile settings.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}
